import React, { useState, useEffect } from "react";
import DoctorChip from "./DoctorChip";
import "./Leave.css";
import * as ApiService from "../services/ApiService";
import { useAuth0 } from "@auth0/auth0-react";

function Leave() {
  const { getAccessTokenSilently } = useAuth0();
  const [selectedDoctor, setSelectedDoctor] = useState(null);
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [doctors, setDoctors] = useState([]);
  const [existingLeaves, setExistingLeaves] = useState([]);
  const [isDeleting, setIsDeleting] = useState(false);
  const [leaveType, setLeaveType] = useState("Annual Leave");
  const [customLeaveType, setCustomLeaveType] = useState("");

  useEffect(() => {
    fetchDoctors();
  }, []);

  useEffect(() => {
    if (selectedDoctor) {
      fetchDoctorLeaves();
    }
  }, [selectedDoctor]);

  const fetchDoctors = async () => {
    try {
      const data = await ApiService.fetchDoctors(getAccessTokenSilently);
      setDoctors(data);
      if (data.length > 0) {
        setSelectedDoctor(data[0].staffId);
      }
    } catch (error) {
      console.error("Error fetching doctors:", error);
    }
  };

  const fetchDoctorLeaves = async () => {
    try {
      const data = await ApiService.fetchLeaves(
        getAccessTokenSilently,
        selectedDoctor
      );
      setExistingLeaves(data);
    } catch (error) {
      console.error("Error fetching leaves:", error);
    }
  };

  const handleAddLeave = async () => {
    if (!selectedDoctor || !startDate) return;

    // If no endDate is selected, use startDate for single-day leave
    const effectiveEndDate = endDate || startDate;

    try {
      await ApiService.addLeave(
        getAccessTokenSilently,
        selectedDoctor,
        startDate.toISOString().split("T")[0],
        effectiveEndDate.toISOString().split("T")[0],
        leaveType,
        leaveType === "Other" ? customLeaveType : null
      );

      // Clear selection and refresh leaves
      setStartDate(null);
      setEndDate(null);
      setLeaveType("Annual Leave");
      setCustomLeaveType("");
      fetchDoctorLeaves();
    } catch (error) {
      console.error("Error adding leave:", error);
    }
  };

  const handleDoctorClick = (doctorId) => {
    setSelectedDoctor(doctorId);
    setStartDate(null);
    setEndDate(null);
  };

  const handleDateClick = (date) => {
    // If the date is already a leave, don't allow selection
    if (isDateInExistingLeave(date)) {
      return;
    }

    // Create new date objects with time set to noon to avoid timezone issues
    const newDate = new Date(
      date.getFullYear(),
      date.getMonth(),
      date.getDate(),
      12,
      0,
      0
    );

    if (!startDate || (startDate && endDate)) {
      setStartDate(newDate);
      setEndDate(null);
    } else {
      // Check if there are any existing leaves between start and potential end date
      const hasOverlappingLeaves = existingLeaves.some((leave) => {
        const leaveStart = new Date(leave.startDate + "T00:00:00");
        const leaveEnd = new Date(leave.endDate + "T00:00:00");

        // If the leave starts after our start date and before/on our potential end date
        // OR if the leave ends after our start date and before/on our potential end date
        return (
          (leaveStart >= startDate && leaveStart <= newDate) ||
          (leaveEnd >= startDate && leaveEnd <= newDate) ||
          (leaveStart <= startDate && leaveEnd >= newDate)
        );
      });

      if (!hasOverlappingLeaves && newDate > startDate) {
        setEndDate(newDate);
      } else {
        // If there's an overlapping leave or the date is before start date,
        // treat this as a new start date
        setStartDate(newDate);
        setEndDate(null);
      }
    }
  };

  const getDaysInMonth = (date) => {
    return new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();
  };

  const getFirstDayOfMonth = (date) => {
    return new Date(date.getFullYear(), date.getMonth(), 1).getDay();
  };

  const generateCalendarDays = (monthDate) => {
    const daysInMonth = getDaysInMonth(monthDate);
    const firstDay = getFirstDayOfMonth(monthDate);
    const days = [];

    for (let i = 0; i < firstDay; i++) {
      days.push(null);
    }

    for (let day = 1; day <= daysInMonth; day++) {
      // Create date with noon time to avoid timezone issues
      days.push(
        new Date(monthDate.getFullYear(), monthDate.getMonth(), day, 12, 0, 0)
      );
    }

    return days;
  };

  const isDateInRange = (date) => {
    if (!startDate || !date) return false;
    if (!endDate) return date.getTime() === startDate.getTime();

    // Compare dates using noon time to ensure consistent comparison
    const compareDate = new Date(
      date.getFullYear(),
      date.getMonth(),
      date.getDate(),
      12,
      0,
      0
    );
    const compareStart = new Date(
      startDate.getFullYear(),
      startDate.getMonth(),
      startDate.getDate(),
      12,
      0,
      0
    );
    const compareEnd = new Date(
      endDate.getFullYear(),
      endDate.getMonth(),
      endDate.getDate(),
      12,
      0,
      0
    );

    return compareDate >= compareStart && compareDate <= compareEnd;
  };

  const isDateInExistingLeave = (date) => {
    if (!date) return false;
    return existingLeaves.some((leave) => {
      const leaveStart = new Date(leave.startDate + "T00:00:00"); // Adjusted for local midnight
      const leaveEnd = new Date(leave.endDate + "T00:00:00"); // Adjusted for local midnight

      // Compare dates using noon time to ensure consistent comparison
      const compareDate = new Date(
        date.getFullYear(),
        date.getMonth(),
        date.getDate(),
        12,
        0,
        0
      );
      const compareStart = new Date(
        leaveStart.getFullYear(),
        leaveStart.getMonth(),
        leaveStart.getDate(),
        12,
        0,
        0
      );
      const compareEnd = new Date(
        leaveEnd.getFullYear(),
        leaveEnd.getMonth(),
        leaveEnd.getDate(),
        12,
        0,
        0
      );

      return compareDate >= compareStart && compareDate <= compareEnd;
    });
  };

  const nextMonth = () => {
    setCurrentMonth(
      new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1)
    );
  };

  const prevMonth = () => {
    setCurrentMonth(
      new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1)
    );
  };

  const formatMonth = (date) => {
    return date.toLocaleString("default", { month: "long", year: "numeric" });
  };

  const handleDeleteLeave = async (leaveId) => {
    if (isDeleting) return; // Prevent multiple clicks

    try {
      setIsDeleting(true);
      await ApiService.deleteLeave(getAccessTokenSilently, leaveId);

      // Immediately update the UI by filtering out the deleted leave
      setExistingLeaves((prevLeaves) =>
        prevLeaves.filter((leave) => leave.leaveId !== leaveId)
      );

      // Then refresh the data from the server
      await fetchDoctorLeaves();
    } catch (error) {
      console.error("Error deleting leave:", error);
      alert("Failed to delete leave. Please try again.");
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <div className="page-container">
      <h2 className="section-header">
        Leave Management
        <div className="info-box">
          New schedules will automatically avoid assigning calls during doctors'
          leave periods.
        </div>
      </h2>
      <div className="combined-panel">
        <div className="leave-content">
          {doctors.length === 0 ? (
            <div className="no-doctors-message">
              No doctors have been added yet. Please add doctors in the Staff
              Management section first.
            </div>
          ) : (
            <>
              <div>
                <div className="doctor-selection-grid" style={{ marginTop: 0 }}>
                  {doctors.map((doctor) => (
                    <DoctorChip
                      key={doctor.staffId}
                      doctor={doctor}
                      selectable
                      selected={selectedDoctor === doctor.staffId}
                      onClick={() => handleDoctorClick(doctor.staffId)}
                    />
                  ))}
                </div>
              </div>

              <div className="calendars-container">
                {selectedDoctor && (
                  <>
                    <div className="selected-doctor-heading">
                      <div className="selected-doctor-title">
                        {
                          doctors.find((d) => d.staffId === selectedDoctor)
                            ?.abbreviation
                        }
                        's Leave Schedule
                      </div>
                    </div>

                    <div className="calendar-navigation">
                      <button onClick={prevMonth}>&lt;</button>
                      <span>{formatMonth(currentMonth)}</span>
                      <span>
                        {formatMonth(
                          new Date(
                            currentMonth.getFullYear(),
                            currentMonth.getMonth() + 1
                          )
                        )}
                      </span>
                      <button onClick={nextMonth}>&gt;</button>
                    </div>

                    <div className="calendar">
                      <div className="calendar-grid">
                        <div className="weekday">SUN</div>
                        <div className="weekday">MON</div>
                        <div className="weekday">TUE</div>
                        <div className="weekday">WED</div>
                        <div className="weekday">THU</div>
                        <div className="weekday">FRI</div>
                        <div className="weekday">SAT</div>
                        {generateCalendarDays(currentMonth).map(
                          (date, index) => (
                            <div
                              key={index}
                              className={`calendar-day ${
                                date ? "active" : ""
                              } ${isDateInRange(date) ? "selected" : ""} ${
                                isDateInExistingLeave(date)
                                  ? "existing-leave"
                                  : ""
                              }`}
                              onClick={() => date && handleDateClick(date)}
                            >
                              {date ? date.getDate() : ""}
                            </div>
                          )
                        )}
                      </div>
                    </div>

                    <div className="calendar">
                      <div className="calendar-grid">
                        <div className="weekday">SUN</div>
                        <div className="weekday">MON</div>
                        <div className="weekday">TUE</div>
                        <div className="weekday">WED</div>
                        <div className="weekday">THU</div>
                        <div className="weekday">FRI</div>
                        <div className="weekday">SAT</div>
                        {generateCalendarDays(
                          new Date(
                            currentMonth.getFullYear(),
                            currentMonth.getMonth() + 1
                          )
                        ).map((date, index) => (
                          <div
                            key={index}
                            className={`calendar-day ${date ? "active" : ""} ${
                              isDateInRange(date) ? "selected" : ""
                            } ${
                              isDateInExistingLeave(date)
                                ? "existing-leave"
                                : ""
                            }`}
                            onClick={() => date && handleDateClick(date)}
                          >
                            {date ? date.getDate() : ""}
                          </div>
                        ))}
                      </div>
                    </div>
                  </>
                )}
              </div>

              <div className="leave-controls">
                <div className="leave-type-selector">
                  <select
                    value={leaveType}
                    onChange={(e) => setLeaveType(e.target.value)}
                    className="leave-type-dropdown"
                  >
                    <option value="Annual Leave">Annual Leave</option>
                    <option value="Training">Training</option>
                    <option value="Requested No-Call">Requested No-Call</option>
                    <option value="Rotated Out">Rotated Out</option>
                    <option value="Other">Other</option>
                  </select>
                  {leaveType === "Other" && (
                    <input
                      type="text"
                      value={customLeaveType}
                      onChange={(e) => setCustomLeaveType(e.target.value)}
                      placeholder="Specify leave type..."
                      className="custom-leave-type-input"
                    />
                  )}
                </div>
                <button
                  className="add-leave-button"
                  disabled={
                    !selectedDoctor ||
                    !startDate ||
                    (leaveType === "Other" && !customLeaveType)
                  }
                  onClick={handleAddLeave}
                >
                  Add Leave
                </button>
              </div>
            </>
          )}
        </div>

        <div className="leaves-list">
          <h3>Existing Leaves</h3>
          {existingLeaves.length === 0 ? (
            <div className="no-leaves-message">
              No leaves have been scheduled for{" "}
              {doctors.find((d) => d.staffId === selectedDoctor)
                ?.abbreviation || "this doctor"}{" "}
              yet.
            </div>
          ) : (
            <div className="leaves-grid">
              {existingLeaves.map((leave) => (
                <div key={leave.leaveId} className="leave-item">
                  <div className="leave-dates">
                    <span className="leave-date-range">
                      {leave.startDate === leave.endDate ? (
                        <strong>{leave.endDate}</strong>
                      ) : (
                        <>
                          <strong>{leave.startDate}</strong> to{" "}
                          <strong>{leave.endDate}</strong>
                        </>
                      )}
                    </span>
                  </div>
                  <div className="leave-row">
                    <span
                      className={`leave-type ${leave.leaveType
                        .toLowerCase()
                        .replace(" ", "-")}`}
                    >
                      {leave.leaveType === "Other"
                        ? leave.customLeaveType
                        : leave.leaveType}
                    </span>
                    <button
                      className="delete-leave-button"
                      onClick={() => handleDeleteLeave(leave.leaveId)}
                      disabled={isDeleting}
                    >
                      {isDeleting ? "Deleting..." : "Delete"}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default Leave;
